import { translateError } from '@/i18n/errors';


import { defineStore } from 'pinia';
import ClubService from '@/services/api/models/ClubService';
import { useDataSourceStore } from '@/stores/dataSource';
import MockService from '@/services/MockService';
import { translate } from '@/i18n';

/**
 * Хранит контекст текущего клуба и кэш клубов по slug,
 * чтобы не дергать API при каждом переходе внутри /clubs/:slug/*.
 */
export const useClubStore = defineStore('club', {
  state: () => ({
    currentSlug: null,
    currentClub: null,
    currentDataSource: null,
    requestId: 0,
    // cache: { [slug]: clubPlainObject }
    bySlug: {},
    loading: false,
    error: null,
    loadFailed: false,
  }),

  getters: {
    isReady: (state) => !!state.currentClub && !!state.currentSlug,
    getBySlug: (state) => (slug) => {
      if (!slug) return null;
      if (useDataSourceStore().isMock) {
        return state.currentDataSource === 'mock' && state.currentSlug === slug ? state.currentClub : null;
      }
      return state.bySlug?.[slug] || null;
    },
  },

  actions: {
    /**
     * Гарантирует, что клуб по slug загружен и выставлен как текущий.
     * Загрузка выполняется только если slug изменился и нет кэша.
     * @param {string} slug
     * @returns {Promise<Object|null>}
     */
    async ensureClub(slug) {
      const requestId = ++this.requestId;
      const source = useDataSourceStore().mode;
      const revision = useDataSourceStore().revision;
      const isCurrent = () => requestId === this.requestId && revision === useDataSourceStore().revision;
      const s = (typeof slug === 'string') ? slug.trim() : '';
      if (!s) {
        this.currentSlug = null;
        this.currentClub = null;
        this.loadFailed = false;
        return null;
      }

      // Моковый контекст не должен подменять клуб из базы после смены источника.
      if (this.currentSlug === s && this.currentClub && this.currentDataSource === source) {
        return this.currentClub;
      }

      // Если есть в кэше — используем
      const cached = source === 'db' ? this.bySlug?.[s] : null;
      if (cached) {
        this.currentSlug = s;
        this.currentClub = cached;
        this.currentDataSource = source;
        this.error = null;
        this.loadFailed = false;
        this.loading = false;
        return cached;
      }

      // Иначе грузим (теперь используем getBySlug)
      this.loading = true;
      this.loadFailed = false;
      this.error = null;
      this.currentClub = null;
      this.currentSlug = s;
      this.currentDataSource = source;

      try {
        const club = source === 'mock'
          ? await MockService.getClubBySlug(s)
          : await ClubService.getBySlug(s);
        if (!isCurrent()) return null;

        if (club) {
          if (source === 'db') this.bySlug[s] = club;
          this.currentSlug = s;
          this.currentClub = club;
          return club;
        }

        this.currentSlug = s;
        this.currentClub = null;
        this.error = translate('clubs.notFoundBySlug', { slug: s });
        return null;
      } catch (e) {
        if (!isCurrent()) return null;
        this.loadFailed = true;
        this.error = translateError(e);
        this.currentSlug = s;
        this.currentClub = null;
        return null;
      } finally {
        if (isCurrent()) this.loading = false;
      }
    },

    /**
     * Принудительно установить текущий клуб (например, если уже есть объект из списка).
     * @param {Object} club
     */
    setCurrentClub(club) {
      const slug = club?.slug;
      if (!slug) return;

      this.requestId++;
      this.currentDataSource = useDataSourceStore().mode;
      if (this.currentDataSource === 'db') this.bySlug[slug] = club;
      this.currentSlug = slug;
      this.currentClub = club;
      this.error = null;
      this.loadFailed = false;
    },

    /**
     * Очистить текущий клуб (например, при выходе на /clubs).
     */
    clearCurrentClub() {
      this.requestId++;
      this.currentDataSource = null;
      this.currentSlug = null;
      this.currentClub = null;
      this.loading = false;
      this.error = null;
      this.loadFailed = false;
    },
  },
});
